// Ticket Core commands from the staff pages and the reporter pages (M3 routes).
// Every staff write quotes the revision the page showed (expectedRevision). A 409 revision_conflict
// is handled once, in lib/error-policy.ts: toast "This ticket has changed" and refetch, never retried.

import type { ReporterStatusView } from '@feedback/shared/contracts/query';
import type { CommandResult, ReporterCommandBody, StaffCommandBody } from '@feedback/shared/contracts/tickets';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, seg } from './api';

export interface StaffCommandVars {
  ref: string;
  body: StaffCommandBody;
  /** Toast shown on success. */
  done?: string;
}

/** Refetches everything that shows tickets. */
export function invalidateTickets(queryClient: QueryClient, ref?: string): Promise<void> {
  return Promise.all([
    ref
      ? queryClient.invalidateQueries({ queryKey: ['ticket', ref] })
      : queryClient.invalidateQueries({ queryKey: ['ticket'] }),
    queryClient.invalidateQueries({ queryKey: ['queue'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ]).then(() => undefined);
}

/**
 * POST /api/tickets/:ref/commands. `channel: 'dashboard'` adds `x-feedback-channel: dashboard`, so
 * history records the dashboard (S6).
 */
export function useTicketCommand(options: { channel?: 'dashboard' } = {}) {
  const queryClient = useQueryClient();
  const headers = options.channel === 'dashboard' ? { 'x-feedback-channel': 'dashboard' } : undefined;
  return useMutation({
    mutationFn: ({ ref, body }: StaffCommandVars) =>
      api.post<CommandResult>(`/api/tickets/${seg(ref)}/commands`, body, headers),
    onSuccess: async (_result, vars) => {
      if (vars.done) {
        toast.success(vars.done);
      }
      await invalidateTickets(queryClient, vars.ref);
    },
  });
}

type ReporterAction = ReporterCommandBody['type'];
type ReporterBody = {
  [K in ReporterAction]: Omit<Extract<ReporterCommandBody, { type: K }>, 'expectedRevision'>;
}[ReporterAction];

/**
 * POST /api/my-requests/:publicId/commands, quoting the revision the status view showed. A 409 (the
 * report moved on) is shown by the page; settling refetches the status view either way, so a stale
 * page changes nothing it no longer shows (REQ-9.5).
 */
export function useReporterCommand(view: Pick<ReporterStatusView, 'publicId' | 'revision'>) {
  const queryClient = useQueryClient();
  const { publicId, revision } = view;
  return useMutation({
    meta: { silent: true },
    mutationFn: (body: ReporterBody) => {
      if (revision === null) {
        // No ticket yet: the view offers no actions, so this is a stale page.
        return Promise.reject(new Error('This report is still being triaged. Try again in a moment.'));
      }
      return api.post<CommandResult>(`/api/my-requests/${seg(publicId)}/commands`, {
        ...body,
        expectedRevision: revision,
      });
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-request', publicId] }),
        queryClient.invalidateQueries({ queryKey: ['my-requests'] }),
      ]),
  });
}
